import { useEffect, useMemo, useRef, useState, useDeferredValue } from "react";
import axios from "axios";
import { smartMatch } from "../../utils/arabicSearch";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useDrafts } from "../../contexts/DraftsContext";
import {
  ShoppingCart,
  History,
  Truck,
  Plus,
  X,
  Search,
  Trash2,
  Save,
  Bell,
  ArrowLeft,
  Receipt,
  HandCoins,
  Banknote,
  Loader2,
  Calendar,
  Package,
  AlertTriangle,
  Wallet,
  Users,
  Printer,
  Share2,
  Check,
} from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (iso) => (iso || "").slice(0, 16).replace("T", " ");
const todayStr = () => new Date().toISOString().slice(0, 10);

// =============== Product picker — search + show image, cost, sale price ===============
// When the admin picks a product, we open a tiny quantity prompt so the
// product is attached to the purchase invoice with the RIGHT number of
// cartons up-front (instead of appending qty=1 and forcing the admin to
// scroll down and edit the row). The picker itself stays open — the admin
// closes it manually via the ✕/Done button when they're finished adding.
const ProductPicker = ({ products, onPick, autoFocus }) => {
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const [askQty, setAskQty] = useState(null); // { product, qty }

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return products.slice(0, 12);
    return products
      .filter((p) => (p.name_ar || "").toLowerCase().includes(term))
      .slice(0, 30);
  }, [products, q]);

  const openQtyPrompt = (p) => setAskQty({ product: p, qty: 1 });

  const confirmQty = () => {
    if (!askQty) return;
    const n = Math.max(1, Math.floor(Number(askQty.qty || 0)));
    if (!(n > 0)) return;
    // Pass qty to the parent's addProduct handler so it can attach the
    // item with the requested count in one shot (no follow-up edit needed).
    onPick(askQty.product, n);
    setAskQty(null);
    setQ("");
    setTimeout(() => { try { inputRef.current?.focus(); } catch {} }, 0);
  };

  return (
    <div data-testid="product-picker">
      <div className="relative mb-2">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="purchase-product-search"
          placeholder="ابحث عن المنتج بالاسم..."
          className="w-full pr-9 pl-4 py-2 rounded-full bg-white border-2 border-[#3B82F6] text-sm focus:outline-none"
        />
      </div>
      <div className="max-h-72 overflow-y-auto space-y-1.5">
        {list.length === 0 ? (
          <p className="text-center text-sm text-[#5C6E64] py-4">لا توجد نتائج</p>
        ) : (
          list.map((p) => (
            <button
              key={p.id}
              onClick={() => openQtyPrompt(p)}
              data-testid={`pick-product-${p.id}`}
              className="w-full bg-white hover:bg-[#F0F9FF] border border-[#E4EBE6] hover:border-[#3B82F6] rounded-xl p-2 flex items-center gap-2 text-right transition-colors"
            >
              {p.image_path ? (
                <img src={`${API}/files/${p.image_path}`} alt={p.name_ar} className="w-10 h-10 object-cover rounded-lg shrink-0" />
              ) : (
                <div className="w-10 h-10 bg-[#F3F6F4] rounded-lg flex items-center justify-center shrink-0">
                  <Package className="w-4 h-4 text-[#5C6E64]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-[#111814] truncate">{p.name_ar}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] bg-[#FFF8EC] text-[#a06e10] px-1.5 py-0.5 rounded-full">شراء <span dir="ltr">{fmt(p.cost_price)}</span></span>
                  <span className="text-[10px] bg-[#ECFDF5] text-[#15803d] px-1.5 py-0.5 rounded-full">بيع <span dir="ltr">{fmt(p.price)}</span></span>
                  <span className="text-[10px] text-[#5C6E64]">المخزن: <span dir="ltr" className="font-bold">{p.stock}</span></span>
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Quantity prompt — small centered card floated on top of the picker.
          Uses z-index one above the picker's parent modal so it captures
          clicks cleanly. Enter key confirms, ✕ or backdrop cancels. */}
      {askQty && (
        <div
          className="fixed inset-0 z-[130] bg-black/50 flex items-center justify-center p-3"
          data-testid="qty-prompt-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setAskQty(null); }}
        >
          <div className="bg-white rounded-2xl w-full max-w-xs shadow-2xl overflow-hidden" dir="rtl">
            {/* Header */}
            <div className="p-3 text-white flex items-center gap-2"
              style={{ background: "linear-gradient(135deg, #3B82F6, #1E3A5F)" }}>
              {askQty.product.image_path ? (
                <img src={`${API}/files/${askQty.product.image_path}`} alt="" className="w-10 h-10 object-cover rounded-lg border-2 border-white/50 shrink-0" />
              ) : (
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
                  <Package className="w-5 h-5" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] opacity-80">إضافة لفاتورة الشراء</p>
                <p className="font-black text-sm truncate">{askQty.product.name_ar}</p>
              </div>
              <button
                onClick={() => setAskQty(null)}
                data-testid="qty-prompt-close"
                className="w-8 h-8 rounded-full bg-white/25 hover:bg-white/40 flex items-center justify-center shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-3">
              <label className="block text-xs font-black text-[#1E3A5F] text-center">عدد الكراتين</label>
              <div className="flex items-center gap-2 justify-center">
                <button
                  type="button"
                  onClick={() => setAskQty((s) => ({ ...s, qty: Math.max(1, Number(s.qty || 0) - 1) }))}
                  className="w-11 h-11 rounded-xl bg-red-100 hover:bg-red-200 text-red-700 flex items-center justify-center font-black text-2xl"
                >
                  −
                </button>
                <input
                  type="number"
                  min="1"
                  value={askQty.qty}
                  autoFocus
                  onChange={(e) => setAskQty((s) => ({ ...s, qty: e.target.value === "" ? "" : Math.max(1, Number(e.target.value)) }))}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmQty(); } }}
                  onFocus={(e) => e.target.select()}
                  data-testid="qty-prompt-input"
                  className="w-24 text-center text-2xl font-black py-2 rounded-xl border-2 border-[#3B82F6] focus:outline-none focus:border-[#1E3A5F] text-[#1E3A5F]"
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setAskQty((s) => ({ ...s, qty: Number(s.qty || 0) + 1 }))}
                  className="w-11 h-11 rounded-xl bg-emerald-100 hover:bg-emerald-200 text-emerald-700 flex items-center justify-center font-black text-2xl"
                >
                  +
                </button>
              </div>
              <p className="text-center text-[10px] text-[#5C6E64]">اضغط Enter أو "موافق" للإضافة</p>
            </div>

            {/* Footer */}
            <div className="p-2 grid grid-cols-2 gap-2 bg-slate-50 border-t">
              <button
                onClick={() => setAskQty(null)}
                className="py-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 font-black text-sm"
              >
                إلغاء
              </button>
              <button
                onClick={confirmQty}
                data-testid="qty-prompt-ok"
                disabled={!(Number(askQty.qty) > 0)}
                className="py-2.5 rounded-xl text-white font-black text-sm shadow disabled:opacity-40"
                style={{ background: "linear-gradient(135deg, #3B82F6, #1E3A5F)" }}
              >
                موافق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =============== New Purchase Invoice form ===============
const NewPurchaseInvoice = ({ products, suppliers, onSaved, presetProductIds }) => {
  const { clearDirty, saveDraft, loadDraft } = useDrafts();
  // Hydrate initial state from a previously-stashed draft (chip-resume)
  const stash = useRef(loadDraft("purchase") || null).current;
  const [supplierName, setSupplierName] = useState(stash?.supplierName || "");
  const [supplierId, setSupplierId] = useState(stash?.supplierId || "");
  const [showSuppliers, setShowSuppliers] = useState(false);
  // شراء من زبون: قائمة الزبائن المفعّلين + الزبون المختار حالياً كمُجهّز
  const [customerSuppliers, setCustomerSuppliers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [supplierCustomerId, setSupplierCustomerId] = useState(stash?.supplierCustomerId || "");
  const [items, setItems] = useState(stash?.items || []); // {product_id,name,image_path,quantity,cost_price}
  const [amountPaid, setAmountPaid] = useState(stash?.amountPaid || "");
  const [discount, setDiscount] = useState(stash?.discount || "");
  const [note, setNote] = useState(stash?.note || "");
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const consumedRef = useRef(false);

  // Draft tracking: mark dirty + persist actual field content to localStorage so
  // clicking the chip later truly restores the user's typing.
  useEffect(() => {
    const hasContent =
      items.length > 0 ||
      supplierName.trim() ||
      amountPaid.trim() ||
      discount.trim() ||
      note.trim();
    if (hasContent) {
      saveDraft("purchase", "فاتورة شراء قيد الكتابة", {
        supplierName, supplierId, items, amountPaid, discount, note,
      });
    }
  }, [items, supplierName, supplierId, amountPaid, discount, note, saveDraft]);
  // Note: when user clicks the × on a draft chip OR chooses "discard", clearDirty
  // is called which removes the localStorage entry as well.

  useEffect(() => {
    if (consumedRef.current) return;
    if (!presetProductIds || presetProductIds.length === 0 || !products.length) return;
    const rows = [];
    presetProductIds.forEach((pid) => {
      const p = products.find((x) => x.id === pid);
      if (p) rows.push({ product_id: p.id, name_ar: p.name_ar, image_path: p.image_path, quantity: 1, cost_price: p.cost_price || 0 });
    });
    if (rows.length) {
      setItems((prev) => {
        // Skip ids already present
        const existing = new Set(prev.map((r) => r.product_id));
        const fresh = rows.filter((r) => !existing.has(r.product_id));
        return [...prev, ...fresh];
      });
      consumedRef.current = true;
    }
  }, [presetProductIds, products]);

  // Fetch customer-suppliers (customers flagged with can_buy_from) once.
  useEffect(() => {
    (async () => {
      try {
        const [csRes, allRes] = await Promise.all([
          axios.get(`${API}/admin/customers-as-suppliers`),
          axios.get(`${API}/admin/customers`),
        ]);
        setCustomerSuppliers(csRes.data || []);
        setAllCustomers(allRes.data || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const filteredSuppliers = useMemo(() => {
    const q = supplierName.trim().toLowerCase();
    if (!q) return suppliers.slice(0, 8);
    return suppliers.filter((s) => (s.name || "").toLowerCase().includes(q)).slice(0, 8);
  }, [suppliers, supplierName]);

  // Search customers too — same search box surfaces matching customers so
  // the admin can pick any customer as a supplier without pre-flagging.
  const filteredCustomers = useMemo(() => {
    const q = supplierName.trim();
    if (!q) return [];
    return (allCustomers || [])
      .filter((c) =>
        smartMatch(`${c.full_name || ""} ${c.market_name || ""} ${c.phone || ""} ${c.customer_id || ""}`, q)
      )
      .slice(0, 8);
  }, [allCustomers, supplierName]);

  // When a plain (unflagged) customer is picked from the search, enable
  // `can_buy_from` on-the-fly so the backend accepts the purchase invoice.
  const pickCustomerAsSupplier = async (c) => {
    try {
      if (!c.can_buy_from) {
        await axios.patch(`${API}/admin/customers/${c.id}/can-buy-from`, { enabled: true });
      }
      setSupplierCustomerId(c.id);
      setSupplierName(c.full_name || "");
      setSupplierId("");
      setShowSuppliers(false);
      // Refresh the flagged list so this customer appears next time
      const { data } = await axios.get(`${API}/admin/customers-as-suppliers`);
      setCustomerSuppliers(data || []);
      toast.success(`✓ الشراء من ${c.full_name} — سيتم خصم قيمة الفاتورة من دينه`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل اختيار الزبون");
    }
  };

  const addProduct = (p, qty = 1) => {
    // Keep the picker open so the admin can add multiple products in
    // one session. They close it manually with the ✕ or the Done button
    // when they finish. Also bumps existing rows' quantity instead of
    // creating duplicate rows. `qty` comes from the ProductPicker's
    // quantity prompt (user types number of cartons up-front).
    const n = Math.max(1, Math.floor(Number(qty) || 1));
    setItems((prev) => {
      const existsIdx = prev.findIndex((it) => it.product_id === p.id);
      if (existsIdx >= 0) {
        return prev.map((it, i) => i === existsIdx ? { ...it, quantity: Number(it.quantity || 0) + n } : it);
      }
      return [
        ...prev,
        {
          product_id: p.id,
          name_ar: p.name_ar,
          image_path: p.image_path,
          quantity: n,
          cost_price: p.cost_price || 0,
          sale_price: p.price || 0,
          wholesale_price: p.wholesale_price || 0,
        },
      ];
    });
    toast.success(`✅ ${p.name_ar} × ${n}`, { duration: 900 });
  };

  const updateItem = (idx, k, v) => {
    const next = [...items];
    next[idx][k] = v;
    setItems(next);
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const subtotal = items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.cost_price || 0), 0);
  const total = Math.max(0, subtotal - Number(discount || 0));
  const remaining = Math.max(0, total - Number(amountPaid || 0));

  const submit = async () => {
    if (!supplierCustomerId && !supplierName.trim() && !supplierId) { toast.error("أدخل اسم المُجهّز"); return; }
    if (items.length === 0) { toast.error("أضف منتجًا واحدًا على الأقل"); return; }
    setSaving(true);
    try {
      const payload = {
        // Two-way purchase: prefer customer-supplier id if selected.
        supplier_customer_id: supplierCustomerId || undefined,
        supplier_id: !supplierCustomerId && supplierId ? supplierId : undefined,
        supplier_name: !supplierCustomerId && !supplierId ? supplierName.trim() : undefined,
        items: items.map((it) => ({
          product_id: it.product_id,
          quantity: Number(it.quantity) || 0,
          cost_price: Number(it.cost_price) || 0,
          sale_price: Number(it.sale_price) || 0,
          wholesale_price: Number(it.wholesale_price) || 0,
        })),
        amount_paid: Number(amountPaid) || 0,
        discount: Number(discount) || 0,
        note: note.trim(),
        update_product_cost: true,
      };
      await axios.post(`${API}/admin/purchase-invoices`, payload);
      toast.success("تم حفظ الفاتورة وتحديث المخزن والأسعار العامة (الكلفة/A+/B+) ✓");
      // reset
      setItems([]);
      setAmountPaid("");
      setDiscount("");
      setNote("");
      setSupplierName("");
      setSupplierId("");
      clearDirty("purchase");
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الحفظ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="new-purchase-invoice">
      {/* Supplier */}
      <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6]">
        <label className="text-xs font-bold text-[#1E3A5F] mb-1 block flex items-center gap-1">
          <Truck className="w-3 h-3" /> اسم المُجهّز <span className="text-[#E85D45]">*</span>
        </label>

        {/* Customer-supplier chips: quick pick a customer as the invoice supplier
            (two-way accounting — reduces their debt automatically). */}
        {customerSuppliers.length > 0 && (
          <div className="mb-2 bg-[#F5F3FF] border border-[#7C3AED]/30 rounded-xl p-2">
            <p className="text-[10px] font-bold text-[#6D28D9] mb-1 flex items-center gap-1">
              💠 أو اشترِ من زبون (حساب متبادل — يخفّض دينه تلقائياً):
            </p>
            <div className="flex flex-wrap gap-1">
              {customerSuppliers.map((c) => {
                const active = supplierCustomerId === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => {
                      if (active) {
                        setSupplierCustomerId("");
                      } else {
                        setSupplierCustomerId(c.id);
                        setSupplierName(c.full_name || "");
                        setSupplierId("");
                      }
                    }}
                    data-testid={`pick-cust-supplier-${c.id}`}
                    className={`text-[10px] font-bold rounded-full px-2 py-1 border-2 transition-colors ${
                      active
                        ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                        : "bg-white border-[#7C3AED]/40 text-[#6D28D9] hover:bg-[#EDE9FE]"
                    }`}
                    title={`دين حالي: ${fmt(c.customer_debt)}`}
                  >
                    {active ? "✓ " : ""}{c.full_name || c.phone}
                    {c.market_name && <span className="opacity-70"> — {c.market_name}</span>}
                  </button>
                );
              })}
              {supplierCustomerId && (
                <button
                  onClick={() => { setSupplierCustomerId(""); setSupplierName(""); }}
                  data-testid="clear-cust-supplier"
                  className="text-[10px] font-bold rounded-full px-2 py-1 bg-[#FEE2E2] text-[#B91C1C] hover:bg-[#FECACA]"
                >
                  إلغاء
                </button>
              )}
            </div>
          </div>
        )}

        <div className="relative">
          <input
            value={supplierName}
            onChange={(e) => { setSupplierName(e.target.value); setSupplierId(""); setSupplierCustomerId(""); setShowSuppliers(true); }}
            onFocus={() => setShowSuppliers(true)}
            disabled={!!supplierCustomerId}
            data-testid="purchase-supplier-input"
            placeholder={supplierCustomerId ? "شراء من زبون مُختار (مربّع بنفسجي أعلاه)" : "اكتب الاسم أو اختر من القائمة..."}
            className={`w-full px-3 py-2 rounded-xl border-2 ${supplierCustomerId ? "border-[#7C3AED] bg-[#FAF5FF]" : "border-[#3B82F6]"} ${supplierCustomerId ? "font-bold text-[#6D28D9]" : ""}`}
          />
          {showSuppliers && !supplierCustomerId && (filteredSuppliers.length > 0 || filteredCustomers.length > 0) && (
            <div className="absolute z-10 right-0 left-0 mt-1 bg-white border border-[#E4EBE6] rounded-xl shadow-lg max-h-72 overflow-y-auto">
              {/* Supplier matches (regular) */}
              {filteredSuppliers.map((s) => (
                <button
                  key={`sup-${s.id}`}
                  onClick={() => { setSupplierName(s.name); setSupplierId(s.id); setShowSuppliers(false); }}
                  data-testid={`pick-supplier-${s.id}`}
                  className="w-full text-right px-3 py-2 hover:bg-[#F0F9FF] text-sm flex justify-between items-center border-b border-[#F3F6F4] last:border-b-0"
                >
                  <span className="font-bold flex items-center gap-1">
                    <span className="text-[9px] bg-[#DBEAFE] text-[#1E40AF] rounded-full px-1.5 py-0.5">مُجهّز</span>
                    {s.name}
                  </span>
                  {s.debt > 0 && <span className="text-[10px] text-[#E85D45]">مدين <span dir="ltr">{fmt(s.debt)}</span></span>}
                </button>
              ))}
              {/* Customer matches (auto flag on pick) */}
              {filteredCustomers.map((c) => (
                <button
                  key={`cust-${c.id}`}
                  onClick={() => pickCustomerAsSupplier(c)}
                  data-testid={`pick-cust-as-sup-${c.id}`}
                  className="w-full text-right px-3 py-2 hover:bg-[#FAF5FF] text-sm flex justify-between items-center border-b border-[#F3F6F4] last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <span className="font-bold flex items-center gap-1 flex-wrap">
                      <span className="text-[9px] bg-[#EDE9FE] text-[#6D28D9] rounded-full px-1.5 py-0.5">🔄 زبون</span>
                      {c.full_name}
                    </span>
                    <p className="text-[10px] text-[#5C6E64] truncate">
                      {c.market_name && <>{c.market_name} · </>}
                      <span dir="ltr">{c.phone}</span>
                    </p>
                  </div>
                  {Number(c.customer_debt || 0) !== 0 && (
                    <span className={`text-[10px] whitespace-nowrap ${c.customer_debt > 0 ? "text-[#B91C1C]" : "text-[#1E3A5F]"}`}>
                      {c.customer_debt > 0 ? "دين " : "له "}
                      <span dir="ltr">{fmt(Math.abs(c.customer_debt))}</span>
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Items */}
      <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6]">
        <div className="flex justify-between items-center mb-2">
          <h4 className="font-bold text-[#1E3A5F] flex items-center gap-1">
            <Package className="w-4 h-4" /> بنود الفاتورة <span className="text-xs text-[#5C6E64]">({items.length})</span>
          </h4>
          <button
            onClick={() => setShowPicker(true)}
            data-testid="purchase-add-item-btn"
            className="bg-[#7ED321] hover:bg-[#6cb91d] text-white text-xs font-bold py-1.5 px-3 rounded-full flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> إضافة منتج
          </button>
        </div>
        {items.length > 0 && (
          <div className="bg-[#F0F9FF] border border-[#3B82F6]/30 rounded-xl p-2 mb-2 text-[10px] text-[#1E3A5F] leading-relaxed">
            💡 لما تعدّل سعر <b className="text-[#F5A623]">الكلفة</b> أو <b className="text-emerald-700">A+</b> أو <b className="text-orange-700">B+</b> هنا وتحفظ الفاتورة،
            راح يتحدّث السعر بشكل عام على المنتج بالمخزون. <span className="text-[#5C6E64]">(الطلبات القديمة تبقى بأسعارها الأصلية)</span>
          </div>
        )}
        {items.length === 0 ? (
          <p className="text-center text-sm text-[#5C6E64] py-4">لم تتم إضافة منتجات بعد</p>
        ) : (
          <div className="space-y-1.5">
            {items.map((it, i) => (
              <div key={i} data-testid={`purchase-row-${i}`} className="bg-[#F8FAFC] rounded-xl p-2 flex items-center gap-2">
                {it.image_path ? (
                  <img src={`${API}/files/${it.image_path}`} alt="" className="w-10 h-10 object-cover rounded-lg" />
                ) : (
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center"><Package className="w-4 h-4 text-[#5C6E64]" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate">{it.name_ar}</p>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    <input
                      type="number"
                      value={it.quantity}
                      onChange={(e) => updateItem(i, "quantity", e.target.value)}
                      data-testid={`purchase-qty-${i}`}
                      className="w-16 px-2 py-1 text-xs rounded-lg border border-[#3B82F6] text-center"
                      placeholder="عدد"
                      dir="ltr"
                    />
                    <span className="text-[10px] text-[#5C6E64]">×</span>
                    <input
                      type="number"
                      value={it.cost_price}
                      onChange={(e) => updateItem(i, "cost_price", e.target.value)}
                      data-testid={`purchase-cost-${i}`}
                      title="سعر الشراء (الكلفة)"
                      className="w-24 px-2 py-1 text-xs rounded-lg border border-[#F5A623] text-center"
                      placeholder="كلفة"
                      dir="ltr"
                    />
                    <span className="text-[10px] text-[#5C6E64]">=</span>
                    <span className="font-bold text-xs text-[#1E3A5F]" dir="ltr">{fmt(Number(it.quantity || 0) * Number(it.cost_price || 0))}</span>
                  </div>
                  {/* 🏷️ أسعار البيع A+ / B+ — لما تتحدث هنا وتحفظ الفاتورة،
                      يتحدث السعر بشكل عام على المنتج (الطلبات القديمة تبقى بأسعارها) */}
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    <span className="text-[9px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full font-bold">A+</span>
                    <input
                      type="number"
                      value={it.sale_price ?? 0}
                      onChange={(e) => updateItem(i, "sale_price", e.target.value)}
                      data-testid={`purchase-sale-a-${i}`}
                      title="سعر البيع لزبائن A+ — يتحدّث بشكل عام لما تحفظ الفاتورة"
                      className="w-24 px-2 py-1 text-xs rounded-lg border-2 border-emerald-400 text-center font-bold text-emerald-700 bg-emerald-50/40"
                      placeholder="سعر A+"
                      dir="ltr"
                    />
                    <span className="text-[9px] bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded-full font-bold">B+</span>
                    <input
                      type="number"
                      value={it.wholesale_price ?? 0}
                      onChange={(e) => updateItem(i, "wholesale_price", e.target.value)}
                      data-testid={`purchase-sale-b-${i}`}
                      title="سعر البيع لزبائن B+ (جملة) — يتحدّث بشكل عام لما تحفظ الفاتورة"
                      className="w-24 px-2 py-1 text-xs rounded-lg border-2 border-orange-400 text-center font-bold text-orange-700 bg-orange-50/40"
                      placeholder="سعر B+"
                      dir="ltr"
                    />
                  </div>
                </div>
                <button onClick={() => removeItem(i)} data-testid={`purchase-row-del-${i}`} className="text-[#E85D45] hover:bg-[#E85D45]/10 rounded p-1">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Totals */}
      <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-3 space-y-2">
        <div className="flex justify-between text-xs"><span>المجموع الفرعي</span><span dir="ltr">{fmt(subtotal)}</span></div>
        <div className="flex items-center justify-between text-xs">
          <span>الخصم</span>
          <input
            type="number"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            data-testid="purchase-discount"
            className="w-24 px-2 py-1 rounded-lg text-black text-center"
            placeholder="0"
            dir="ltr"
          />
        </div>
        <div className="flex justify-between font-bold text-base border-t border-white/30 pt-1.5"><span>الإجمالي</span><span dir="ltr">{fmt(total)}</span></div>

        {/* Payment mode chips — 3 vibrant buttons.
            نقداً (green): amount_paid = total  → paid in full, zero debt
            آجل (red):    amount_paid = 0      → whole invoice becomes debt
            جزئي (amber): keep manual input    → admin decides split
            The chip glows when active so it's obvious which mode is on. */}
        <div className="grid grid-cols-3 gap-2 pt-1">
          {(() => {
            const paid = Number(amountPaid) || 0;
            const isCash = paid >= total && total > 0;
            const isDebt = paid === 0 || amountPaid === "";
            const isPartial = !isCash && !isDebt;
            return (
              <>
                <button
                  type="button"
                  onClick={() => setAmountPaid(String(total))}
                  data-testid="purchase-mode-cash"
                  className={`py-2.5 rounded-xl font-black text-sm flex items-center justify-center gap-1.5 transition-all border-2 ${isCash
                    ? "bg-emerald-400 text-white border-emerald-200 shadow-lg scale-105"
                    : "bg-white/15 hover:bg-white/25 text-white border-white/30"}`}
                >
                  💵 نقداً
                </button>
                <button
                  type="button"
                  onClick={() => setAmountPaid("")}
                  data-testid="purchase-mode-debt"
                  className={`py-2.5 rounded-xl font-black text-sm flex items-center justify-center gap-1.5 transition-all border-2 ${isDebt
                    ? "bg-red-500 text-white border-red-200 shadow-lg scale-105"
                    : "bg-white/15 hover:bg-white/25 text-white border-white/30"}`}
                >
                  📅 آجل
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Seed partial with half of the total if empty, so the
                    // admin sees a sensible starting point and just tweaks it.
                    if (isCash || isDebt) setAmountPaid(String(Math.floor(total / 2)));
                  }}
                  data-testid="purchase-mode-partial"
                  className={`py-2.5 rounded-xl font-black text-sm flex items-center justify-center gap-1.5 transition-all border-2 ${isPartial
                    ? "bg-amber-400 text-white border-amber-200 shadow-lg scale-105"
                    : "bg-white/15 hover:bg-white/25 text-white border-white/30"}`}
                >
                  ⚖️ جزئي
                </button>
              </>
            );
          })()}
        </div>

        <div className="flex items-center justify-between text-xs pt-1">
          <span>المدفوع نقداً الآن</span>
          <input
            type="number"
            value={amountPaid}
            onChange={(e) => setAmountPaid(e.target.value)}
            data-testid="purchase-amount-paid"
            className="w-24 px-2 py-1 rounded-lg text-black text-center"
            placeholder="0"
            dir="ltr"
          />
        </div>
        <div className="flex justify-between font-bold text-base text-[#FFEC99]"><span>المتبقّي على المُجهّز</span><span dir="ltr">{fmt(remaining)}</span></div>
      </div>

      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="ملاحظة (اختياري)"
        data-testid="purchase-note"
        className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6]"
      />

      <button
        onClick={submit}
        disabled={saving}
        data-testid="purchase-save-btn"
        className="w-full bg-[#7ED321] hover:bg-[#6cb91d] disabled:opacity-50 text-white font-bold py-2.5 rounded-full flex items-center justify-center gap-2"
      >
        <Save className="w-4 h-4" />
        {saving ? "جاري الحفظ..." : "حفظ فاتورة الشراء"}
      </button>

      {showPicker && (
        <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3" onClick={() => setShowPicker(false)} data-testid="purchase-picker-modal">
          <div className="bg-white rounded-3xl w-full max-w-lg p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="font-bold text-[#1E3A5F] shrink-0">اختر منتجًا</h3>
                {items.length > 0 && (
                  <span className="text-[11px] font-black bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                    ✅ {items.length} منتج بالفاتورة
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowPicker(false)}
                data-testid="purchase-picker-close"
                className="text-xs font-black bg-[#1E3A5F] text-white px-3 py-1.5 rounded-full flex items-center gap-1 shrink-0"
              >
                <Check className="w-3.5 h-3.5" /> تم
              </button>
            </div>
            <ProductPicker products={products} onPick={addProduct} autoFocus />
          </div>
        </div>
      )}
    </div>
  );
};

// =============== Invoice Detail Modal ===============
// Exported so external tabs (like PurchasesReportTab) can reuse the full
// purchase-invoice detail modal (view/edit/delete) instead of duplicating it.
export const InvoiceDetailModal = ({ invoiceId, products, onClose, onDeleted }) => {
  const [inv, setInv] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/purchase-invoices/${invoiceId}`);
        setInv(data);
      } catch { toast.error("فشل التحميل"); onClose(); }
    })();
  }, [invoiceId]);

  const remove = async () => {
    if (!(await confirmDialog("حذف هذه الفاتورة سيُعيد طرح كميات المخزن. هل أنت متأكد؟", { force: true, tone: "danger" }))) return;
    try {
      await axios.delete(`${API}/admin/purchase-invoices/${invoiceId}`);
      toast.success("تم الحذف وتمت إعادة طرح المخزن");
      onDeleted();
      onClose();
    } catch { toast.error("فشل الحذف"); }
  };

  // Inline edit mode: admin can change quantity/price/remove items.
  // Saves via PATCH /admin/purchase-invoices/{id} which reconciles stock
  // and supplier ledger.
  const [editing, setEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showAddPicker, setShowAddPicker] = useState(false);
  useEffect(() => {
    if (inv) setEditItems(inv.items.map((it) => {
      // Prime A+/B+ from snapshot (if invoice was saved with them) OR from
      // the current product record (initial edit before we had snapshots).
      const prod = (products || []).find((p) => p.id === it.product_id) || {};
      return {
        ...it,
        sale_price: it.sale_price_snapshot ?? prod.price ?? 0,
        wholesale_price: it.wholesale_price_snapshot ?? prod.wholesale_price ?? 0,
      };
    }));
  }, [inv, products]);
  const updateItem = (idx, field, val) => {
    setEditItems((prev) => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));
  };
  const removeItemRow = (idx) => setEditItems((prev) => prev.filter((_, i) => i !== idx));
  const addNewItem = (p, qty = 1) => {
    const n = Math.max(1, Math.floor(Number(qty) || 1));
    setEditItems((prev) => {
      // If product already exists, increment its quantity instead of duplicating
      const existsIdx = prev.findIndex((it) => it.product_id === p.id);
      if (existsIdx >= 0) {
        return prev.map((it, i) => i === existsIdx ? { ...it, quantity: Number(it.quantity || 0) + n } : it);
      }
      return [...prev, {
        product_id: p.id,
        product_name_ar: p.name_ar,
        product_image_path: p.image_path,
        quantity: n,
        cost_price: p.cost_price || 0,
        sale_price: p.price || 0,
        wholesale_price: p.wholesale_price || 0,
      }];
    });
    // Keep the picker open — admin closes it manually when finished.
    toast.success(`✅ ${p.name_ar} × ${n}`, { duration: 900 });
  };
  const [editSupplierName, setEditSupplierName] = useState("");
  useEffect(() => {
    if (inv) setEditSupplierName(inv.supplier_name || "");
  }, [inv]);
  const saveEdits = async () => {
    const items = editItems.map((it) => ({
      product_id: it.product_id,
      quantity: parseInt(it.quantity) || 0,
      cost_price: parseFloat(it.cost_price) || 0,
      sale_price: parseFloat(it.sale_price) || 0,
      wholesale_price: parseFloat(it.wholesale_price) || 0,
    })).filter((it) => it.quantity > 0);
    if (items.length === 0) { toast.error("لا توجد بنود صالحة"); return; }
    setSaving(true);
    try {
      const payload = { items };
      // Only send supplier_name if it actually changed AND the invoice is
      // NOT linked to a customer (backend rejects rename on linked invoices).
      const trimmed = (editSupplierName || "").trim();
      if (trimmed && trimmed !== (inv?.supplier_name || "") && !inv?.supplier_customer_id) {
        payload.supplier_name = trimmed;
      }
      await axios.patch(`${API}/admin/purchase-invoices/${invoiceId}`, payload);
      toast.success("تم التعديل — المخزن + حساب المُجهّز + أسعار المنتجات العامة تحدّثت ✓");
      onDeleted();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل التعديل"); }
    finally { setSaving(false); }
  };

  // Build a plain-text invoice for sharing on WhatsApp
  const buildWhatsappText = () => {
    if (!inv) return "";
    const lines = [];
    lines.push(`*فاتورة شراء — جملتي*`);
    lines.push(`رقم الفاتورة: ${inv.invoice_number}`);
    lines.push(`المُجهّز: ${inv.supplier_name}`);
    lines.push(`التاريخ: ${fmtDate(inv.created_at)}`);
    lines.push(`────────────────`);
    inv.items.forEach((it, i) => {
      lines.push(`${i + 1}. ${it.product_name_ar}`);
      lines.push(`   ${it.quantity} × ${fmt(it.cost_price)} = ${fmt(it.line_total)} د.ع`);
    });
    lines.push(`────────────────`);
    lines.push(`المجموع الفرعي: ${fmt(inv.subtotal)} د.ع`);
    if (inv.discount > 0) lines.push(`الخصم: −${fmt(inv.discount)} د.ع`);
    lines.push(`*الإجمالي: ${fmt(inv.total)} د.ع*`);
    lines.push(`المدفوع: ${fmt(inv.amount_paid)} د.ع`);
    if (inv.amount_remaining > 0) lines.push(`*المتبقّي: ${fmt(inv.amount_remaining)} د.ع*`);
    if (inv.note) lines.push(`ملاحظة: ${inv.note}`);
    return lines.join("\n");
  };

  const shareWhatsapp = () => {
    const txt = encodeURIComponent(buildWhatsappText());
    window.open(`https://wa.me/?text=${txt}`, "_blank");
  };

  const printInvoice = () => {
    if (!inv) return;
    const w = window.open("", "_blank", "width=820,height=900");
    if (!w) { toast.error("امنع المتصفح من حجب النوافذ المنبثقة"); return; }
    const rowsHtml = inv.items.map((it, i) => `
      <tr>
        <td>${i + 1}</td>
        <td style="text-align:right">${(it.product_name_ar || "").replace(/</g, "&lt;")}</td>
        <td>${it.quantity}</td>
        <td>${fmt(it.cost_price)}</td>
        <td>${fmt(it.line_total)}</td>
      </tr>`).join("");
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"/>
      <title>فاتورة شراء #${inv.invoice_number}</title>
      <style>
        body{font-family:'Tahoma','Segoe UI',Arial,sans-serif;padding:24px;color:#1E3A5F;}
        h1{margin:0 0 4px;font-size:22px}
        .meta{font-size:12px;color:#5C6E64;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;margin-top:8px;font-size:13px}
        th,td{border:1px solid #CBD5E1;padding:6px 8px;text-align:center}
        th{background:#1E3A5F;color:#fff}
        .totals{margin-top:14px;font-size:14px;width:300px;margin-right:auto}
        .totals div{display:flex;justify-content:space-between;padding:3px 0;border-bottom:1px dashed #CBD5E1}
        .totals .grand{font-weight:900;font-size:16px;color:#0F172A;border-bottom:2px solid #1E3A5F}
        .remaining{color:#E85D45;font-weight:700}
        .note{margin-top:14px;background:#FFF8EC;border:1px solid #F5A623;padding:8px;border-radius:8px;font-size:12px}
        @media print {button{display:none}}
      </style></head>
      <body>
        <h1>فاتورة شراء #${inv.invoice_number}</h1>
        <div class="meta">
          المُجهّز: <b>${(inv.supplier_name || "").replace(/</g, "&lt;")}</b> · التاريخ: ${fmtDate(inv.created_at)}
        </div>
        <table>
          <thead><tr><th>#</th><th>المنتج</th><th>الكمية</th><th>سعر الشراء</th><th>الإجمالي</th></tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
        <div class="totals">
          <div><span>المجموع الفرعي</span><span dir="ltr">${fmt(inv.subtotal)}</span></div>
          ${inv.discount > 0 ? `<div><span>الخصم</span><span dir="ltr">−${fmt(inv.discount)}</span></div>` : ""}
          <div class="grand"><span>الإجمالي</span><span dir="ltr">${fmt(inv.total)}</span></div>
          <div><span>المدفوع</span><span dir="ltr">${fmt(inv.amount_paid)}</span></div>
          ${inv.amount_remaining > 0 ? `<div class="remaining"><span>المتبقّي على المُجهّز</span><span dir="ltr">${fmt(inv.amount_remaining)}</span></div>` : ""}
        </div>
        ${inv.note ? `<div class="note">📝 ${inv.note.replace(/</g, "&lt;")}</div>` : ""}
        <script>window.onload = () => setTimeout(() => window.print(), 250);</script>
      </body></html>`;
    w.document.open(); w.document.write(html); w.document.close();
  };

  // Supplier discount — admin applies a discount the supplier granted on
  // this invoice. Reduces invoice total + supplier debt + ADDS to app
  // profit (since our effective cost dropped).
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountAmount, setDiscountAmount] = useState("");
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const applySupplierDiscount = async () => {
    const amt = parseFloat(discountAmount);
    if (!amt || amt <= 0) { toast.error("أدخل مبلغ صحيح"); return; }
    if (inv && amt > Number(inv.total || 0)) {
      toast.error("الخصم أكبر من قيمة الفاتورة"); return;
    }
    setApplyingDiscount(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/purchase-invoices/${invoiceId}/supplier-discount`,
        { amount: amt }
      );
      toast.success(data.message || "تم تطبيق الخصم");
      // refresh invoice
      const { data: fresh } = await axios.get(`${API}/admin/purchase-invoices/${invoiceId}`);
      setInv(fresh);
      setShowDiscountModal(false);
      setDiscountAmount("");
      onDeleted && onDeleted(); // re-fetch list so it shows updated total
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تطبيق الخصم");
    } finally { setApplyingDiscount(false); }
  };

  // ─── Link an existing purchase invoice to a customer's account
  // (two-way accounting retro-link). Fixes the "I bought from Sultan but it
  // didn't show on his statement" case where the invoice was originally
  // created against a plain supplier name.
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkCustomers, setLinkCustomers] = useState([]);
  const [linkSearch, setLinkSearch] = useState("");
  const [linking, setLinking] = useState(false);
  const openLinkModal = async () => {
    setShowLinkModal(true);
    try {
      const { data } = await axios.get(`${API}/admin/customers-as-suppliers`);
      setLinkCustomers(data || []);
    } catch { toast.error("فشل جلب قائمة الزبائن"); }
  };
  const linkToCustomer = async (customerId, displayName) => {
    setLinking(true);
    try {
      await axios.post(`${API}/admin/purchases/${invoiceId}/link-customer`, {
        customer_id: customerId || null,
      });
      toast.success(
        customerId
          ? `✓ تم ربط الفاتورة بحساب ${displayName || "الزبون"} — نزلت بكشف حسابه`
          : "✓ تم إلغاء الربط"
      );
      // Refresh the invoice + notify parent to re-fetch list
      const { data: fresh } = await axios.get(`${API}/admin/purchase-invoices/${invoiceId}`);
      setInv(fresh);
      setShowLinkModal(false);
      onDeleted && onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الربط");
    } finally { setLinking(false); }
  };
  const filteredLinkCustomers = linkSearch.trim()
    ? linkCustomers.filter((c) => {
        const q = linkSearch.trim().toLowerCase();
        return (
          (c.full_name || "").toLowerCase().includes(q) ||
          (c.market_name || "").toLowerCase().includes(q) ||
          (c.phone || "").includes(q)
        );
      })
    : linkCustomers;

  if (!inv) return (
    <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center" data-testid="invoice-detail-loading"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>
  );

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-2" onClick={onClose} data-testid="invoice-detail-modal" dir="rtl">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white p-4 sticky top-0 flex justify-between items-start z-10">
          <div>
            <p className="text-[10px] opacity-80">فاتورة شراء</p>
            <h3 className="font-bold text-lg">#<span dir="ltr">{inv.invoice_number}</span> · {inv.supplier_name}</h3>
            <p className="text-[10px] opacity-90 mt-0.5">{fmtDate(inv.created_at)}</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={shareWhatsapp}
              data-testid="invoice-whatsapp-btn"
              title="مشاركة على واتساب"
              className="bg-[#25D366] hover:bg-[#1ebd5a] rounded-full p-1.5"
            >
              <Share2 className="w-4 h-4" />
            </button>
            <button
              onClick={printInvoice}
              data-testid="invoice-print-btn"
              title="طباعة"
              className="bg-white/20 hover:bg-white/30 rounded-full p-1.5"
            >
              <Printer className="w-4 h-4" />
            </button>
            <button onClick={onClose} data-testid="invoice-detail-close" className="bg-white/20 hover:bg-white/30 rounded-full p-1.5"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-3 space-y-2">
          {editing && !inv?.supplier_customer_id && (
            <div className="bg-[#F0F9FF] rounded-xl p-2 border border-[#0EA5E9]/40">
              <label className="text-[10px] font-bold text-[#0369A1] block mb-1">
                ✏️ اسم المُجهّز (من مين اشترينا)
              </label>
              <input
                type="text"
                value={editSupplierName}
                onChange={(e) => setEditSupplierName(e.target.value)}
                placeholder="اكتب اسم المُجهّز الجديد..."
                data-testid="edit-supplier-name"
                className="w-full px-2 py-1.5 rounded-lg border-2 border-[#0EA5E9]/30 text-sm font-bold focus:outline-none focus:border-[#0EA5E9]"
              />
            </div>
          )}
          {editing ? editItems.map((it, i) => (
            <div key={i} className="bg-[#FEF3C7] rounded-xl p-2 flex items-center gap-1.5 border border-[#F59E0B]/30">
              {it.product_image_path ? (
                <img src={`${API}/files/${it.product_image_path}`} alt="" className="w-10 h-10 rounded-lg object-cover" />
              ) : <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center"><Package className="w-3 h-3" /></div>}
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold truncate">{it.product_name_ar}</p>
                <div className="flex gap-1 mt-1 flex-wrap">
                  <input
                    type="number" min={1} value={it.quantity}
                    onChange={(e) => updateItem(i, "quantity", e.target.value)}
                    placeholder="الكمية" data-testid={`edit-qty-${i}`}
                    className="w-16 px-1 py-0.5 rounded border border-[#F59E0B]/40 text-xs text-center"
                    dir="ltr"
                  />
                  <input
                    type="number" min={0} value={it.cost_price}
                    onChange={(e) => updateItem(i, "cost_price", e.target.value)}
                    placeholder="الكلفة" data-testid={`edit-cost-${i}`}
                    title="سعر الشراء (الكلفة)"
                    className="w-20 px-1 py-0.5 rounded border border-[#F59E0B]/40 text-xs text-center"
                    dir="ltr"
                  />
                </div>
                <div className="flex gap-1 mt-1 flex-wrap items-center">
                  <span className="text-[9px] bg-emerald-200 text-emerald-800 px-1 py-0.5 rounded-full font-bold">A+</span>
                  <input
                    type="number" min={0} value={it.sale_price ?? 0}
                    onChange={(e) => updateItem(i, "sale_price", e.target.value)}
                    placeholder="سعر A+" data-testid={`edit-sale-a-${i}`}
                    title="سعر البيع لزبائن A+ — يتحدّث بشكل عام لما تحفظ التعديل"
                    className="w-20 px-1 py-0.5 rounded border-2 border-emerald-400 text-xs text-center font-bold text-emerald-700 bg-emerald-50/60"
                    dir="ltr"
                  />
                  <span className="text-[9px] bg-orange-200 text-orange-800 px-1 py-0.5 rounded-full font-bold">B+</span>
                  <input
                    type="number" min={0} value={it.wholesale_price ?? 0}
                    onChange={(e) => updateItem(i, "wholesale_price", e.target.value)}
                    placeholder="سعر B+" data-testid={`edit-sale-b-${i}`}
                    title="سعر البيع لزبائن B+ — يتحدّث بشكل عام لما تحفظ التعديل"
                    className="w-20 px-1 py-0.5 rounded border-2 border-orange-400 text-xs text-center font-bold text-orange-700 bg-orange-50/60"
                    dir="ltr"
                  />
                </div>
              </div>
              <button onClick={() => removeItemRow(i)} data-testid={`edit-remove-${i}`} className="text-[#DC2626] hover:bg-[#FEE2E2] p-1 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          )) : inv.items.map((it, i) => (
            <div key={i} className="bg-[#F8FAFC] rounded-xl p-2 flex items-center gap-2">
              {it.product_image_path ? (
                <img src={`${API}/files/${it.product_image_path}`} alt="" className="w-12 h-12 rounded-lg object-cover" />
              ) : <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center"><Package className="w-4 h-4" /></div>}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold truncate">{it.product_name_ar}</p>
                <p className="text-[10px] text-[#5C6E64]" dir="ltr">{it.quantity} × {fmt(it.cost_price)}</p>
              </div>
              <span className="font-bold text-sm text-[#1E3A5F]" dir="ltr">{fmt(it.line_total)}</span>
            </div>
          ))}
          {editing && (
            <button
              onClick={() => setShowAddPicker(true)}
              data-testid="invoice-add-item-btn"
              className="w-full bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-2 rounded-xl flex items-center justify-center gap-1 text-xs"
            >
              <Plus className="w-3.5 h-3.5" /> إضافة منتج للفاتورة
            </button>
          )}
          <div className="bg-[#E0F2FE] rounded-2xl p-3 mt-3 space-y-1">
            <div className="flex justify-between text-xs"><span>المجموع الفرعي</span><span dir="ltr">{fmt(inv.subtotal)}</span></div>
            {inv.discount > 0 && <div className="flex justify-between text-xs text-[#E85D45]"><span>الخصم</span><span dir="ltr">−{fmt(inv.discount)}</span></div>}
            <div className="flex justify-between font-bold border-t border-[#3B82F6]/30 pt-1"><span>الإجمالي</span><span dir="ltr">{fmt(inv.total)}</span></div>
            <div className="flex justify-between text-xs"><span>المدفوع</span><span dir="ltr">{fmt(inv.amount_paid)}</span></div>
            <div className="flex justify-between font-bold text-[#E85D45]"><span>المتبقّي على المُجهّز</span><span dir="ltr">{fmt(inv.amount_remaining)}</span></div>
            {Number(inv.supplier_discount || 0) > 0 && (
              <div className="flex justify-between font-bold text-[#15803D] border-t border-[#22C55E]/30 pt-1 mt-1">
                <span>💰 خصم من المُجهّز (أُضيف للأرباح)</span>
                <span dir="ltr">−{fmt(inv.supplier_discount)}</span>
              </div>
            )}
          </div>
          {inv.note && <p className="text-xs text-[#5C6E64] bg-[#FFF8EC] rounded-xl p-2">📝 {inv.note}</p>}
          {/* Two-way accounting link indicator + retro-link button */}
          <div className={`rounded-xl p-2 border-2 text-[11px] ${inv.supplier_customer_id ? "bg-[#ECFDF5] border-[#22C55E]/40 text-[#065F46]" : "bg-[#FEF3C7] border-[#F59E0B]/40 text-[#78350F]"}`}>
            {inv.supplier_customer_id ? (
              <div className="flex justify-between items-center gap-2">
                <span className="font-bold">🔗 مربوطة بحساب الزبون — {inv.supplier_name}</span>
                <button
                  onClick={() => linkToCustomer(null, "")}
                  disabled={linking}
                  data-testid="invoice-unlink-customer-btn"
                  className="bg-[#E85D45] hover:bg-[#d54d35] text-white text-[10px] font-bold px-2 py-1 rounded-full"
                >
                  فك الربط
                </button>
              </div>
            ) : (
              <div className="flex justify-between items-center gap-2">
                <span className="font-bold">⚠ هذه الفاتورة غير مرتبطة بحساب زبون</span>
                <button
                  onClick={openLinkModal}
                  data-testid="invoice-link-customer-btn"
                  className="bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-[10px] font-bold px-2 py-1 rounded-full"
                >
                  🔗 اربط بحساب زبون
                </button>
              </div>
            )}
          </div>
          {editing ? (
            <div className="flex gap-2 mt-2">
              <button onClick={saveEdits} disabled={saving} data-testid="invoice-save-edit-btn" className="flex-1 bg-[#15803D] hover:bg-[#0f6630] disabled:opacity-50 text-white font-bold py-2 rounded-full flex items-center justify-center gap-1">
                {saving ? "جاري الحفظ..." : "✓ حفظ التعديلات"}
              </button>
              <button onClick={() => { setEditing(false); setEditItems(inv.items.map((it) => ({ ...it }))); }} className="bg-[#F3F6F4] text-[#5C6E64] font-bold px-4 rounded-full">إلغاء</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button onClick={() => setEditing(true)} data-testid="invoice-edit-btn" className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-bold py-2 rounded-full flex items-center justify-center gap-1 text-sm">
                ✏️ تعديل
              </button>
              <button onClick={() => setShowDiscountModal(true)} data-testid="invoice-supplier-discount-btn" className="bg-[#15803D] hover:bg-[#0f6630] text-white font-bold py-2 rounded-full flex items-center justify-center gap-1 text-sm">
                💰 خصم من المُجهّز
              </button>
              <button onClick={shareWhatsapp} data-testid="invoice-whatsapp-footer-btn" className="bg-[#25D366] hover:bg-[#1ebd5a] text-white font-bold py-2 rounded-full flex items-center justify-center gap-1 text-sm">
                <Share2 className="w-4 h-4" /> واتساب
              </button>
              <button onClick={printInvoice} data-testid="invoice-print-footer-btn" className="bg-[#1E3A5F] hover:bg-[#0f2542] text-white font-bold py-2 rounded-full flex items-center justify-center gap-1 text-sm">
                <Printer className="w-4 h-4" /> طباعة
              </button>
              <button onClick={remove} data-testid="invoice-delete-btn" className="col-span-2 bg-[#E85D45] hover:bg-[#d54d35] text-white font-bold py-2 rounded-full flex items-center justify-center gap-1 text-sm">
                <Trash2 className="w-4 h-4" /> حذف
              </button>
            </div>
          )}
        </div>
        {/* Supplier discount modal */}
        {showDiscountModal && (
          <div className="fixed inset-0 z-[125] bg-black/60 flex items-center justify-center p-3" onClick={() => setShowDiscountModal(false)} data-testid="supplier-discount-modal">
            <div className="bg-white rounded-3xl w-full max-w-sm p-4 shadow-2xl" onClick={(e) => e.stopPropagation()} dir="rtl">
              <div className="flex justify-between mb-2">
                <h3 className="font-bold text-[#15803D]">💰 خصم من المُجهّز</h3>
                <button onClick={() => setShowDiscountModal(false)} data-testid="supplier-discount-close"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-xs text-[#5C6E64] mb-2">
                الفاتورة: <b dir="ltr">{fmt(inv.total)} د.ع</b> ·
                المُجهّز: <b>{inv.supplier_name}</b>
              </p>
              <input
                type="number" inputMode="numeric" min={0} max={inv.total}
                value={discountAmount}
                onChange={(e) => setDiscountAmount(e.target.value)}
                placeholder="مثلاً: 100000"
                data-testid="supplier-discount-amount"
                autoFocus dir="ltr"
                className="w-full px-3 py-3 rounded-xl border-2 border-[#15803D]/40 text-lg font-bold text-center focus:outline-none focus:border-[#15803D]"
              />
              {parseFloat(discountAmount) > 0 && (
                <div className="mt-2 bg-[#ECFDF5] border border-[#22C55E]/40 rounded-xl p-2 text-[11px] text-[#065F46]">
                  <p>الإجمالي بعد الخصم: <b dir="ltr">{fmt(Math.max(0, Number(inv.total||0) - parseFloat(discountAmount||0)))} د.ع</b></p>
                  <p className="opacity-80 mt-0.5">📈 الربح يزيد بـ <span dir="ltr">{fmt(parseFloat(discountAmount||0))} د.ع</span> (التكلفة الفعلية قلّت)</p>
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <button onClick={() => setShowDiscountModal(false)} className="flex-1 bg-[#F3F6F4] text-[#5C6E64] font-bold py-2 rounded-full">إلغاء</button>
                <button
                  onClick={applySupplierDiscount}
                  disabled={applyingDiscount}
                  data-testid="supplier-discount-submit"
                  className="flex-1 bg-[#15803D] hover:bg-[#0f6630] disabled:opacity-50 text-white font-bold py-2 rounded-full"
                >
                  {applyingDiscount ? "جاري..." : "✓ تطبيق الخصم"}
                </button>
              </div>
            </div>
          </div>
        )}
        {showAddPicker && (
          <div className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-3" onClick={() => setShowAddPicker(false)} data-testid="invoice-edit-picker-modal">
            <div className="bg-white rounded-3xl w-full max-w-lg p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3 gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="font-bold text-[#1E3A5F] shrink-0">إضافة منتج للفاتورة</h3>
                  {editItems.length > 0 && (
                    <span className="text-[11px] font-black bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                      ✅ {editItems.length} منتج بالفاتورة
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowAddPicker(false)}
                  data-testid="invoice-edit-picker-close"
                  className="text-xs font-black bg-[#1E3A5F] text-white px-3 py-1.5 rounded-full flex items-center gap-1 shrink-0"
                >
                  <Check className="w-3.5 h-3.5" /> تم
                </button>
              </div>
              <ProductPicker products={products || []} onPick={addNewItem} autoFocus />
            </div>
          </div>
        )}
        {/* Retro-link customer picker */}
        {showLinkModal && (
          <div
            className="fixed inset-0 z-[125] bg-black/60 flex items-center justify-center p-3"
            onClick={() => setShowLinkModal(false)}
            data-testid="link-customer-modal"
          >
            <div
              className="bg-white rounded-3xl w-full max-w-md max-h-[80vh] flex flex-col shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              dir="rtl"
            >
              <div className="p-4 pb-2 border-b flex justify-between items-center">
                <div>
                  <h3 className="font-black text-[#7C3AED] text-base">🔗 اربط الفاتورة بحساب زبون</h3>
                  <p className="text-[10px] text-[#5C6E64] mt-0.5">اختر الزبون فتنزل الفاتورة بكشف حسابه</p>
                </div>
                <button onClick={() => setShowLinkModal(false)} data-testid="link-customer-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <input
                type="text"
                value={linkSearch}
                onChange={(e) => setLinkSearch(e.target.value)}
                placeholder="ابحث بالاسم أو المتجر أو الهاتف..."
                data-testid="link-customer-search"
                className="mx-4 mt-3 px-3 py-2 rounded-xl border-2 border-[#7C3AED]/30 text-sm focus:outline-none focus:border-[#7C3AED]"
              />
              <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
                {filteredLinkCustomers.length === 0 && (
                  <div className="text-center text-[#5C6E64] text-xs py-8">
                    {linkCustomers.length === 0
                      ? "لا يوجد زبائن مفعّلين للشراء منهم بعد. فعّل «الشراء من هذا الزبون» من صفحة الأرصدة أولاً."
                      : "لا توجد نتائج"}
                  </div>
                )}
                {filteredLinkCustomers.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => linkToCustomer(c.id, c.market_name || c.full_name)}
                    disabled={linking}
                    data-testid={`link-customer-pick-${c.id}`}
                    className="w-full text-right bg-[#F8FAFC] hover:bg-[#EEF2FF] disabled:opacity-50 rounded-xl px-3 py-2 border border-[#E4EBE6] transition-all"
                  >
                    <div className="flex justify-between items-center">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-[#1E3A5F] truncate">{c.full_name}</p>
                        {c.market_name && (
                          <p className="text-[10px] text-[#E85D45] truncate">{c.market_name}</p>
                        )}
                        <p className="text-[10px] text-[#5C6E64]" dir="ltr">{c.phone}</p>
                      </div>
                      {Number(c.customer_debt || 0) !== 0 && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.customer_debt > 0 ? "bg-[#FEE2E2] text-[#B91C1C]" : "bg-[#DCFCE7] text-[#15803D]"}`} dir="ltr">
                          {fmt(Math.abs(c.customer_debt))} د.ع
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =============== History (previous invoices) ===============
// Feb 28, 2026 revamp — search, colored summary chips, and per-invoice
// visual cues:
//   • blue    → invoice-level info (number, supplier)
//   • green   → total amount / fully paid
//   • red     → amount still owed
//   • orange  → payment on invoice
// Search runs client-side with Arabic-aware `smartMatch` so admins can
// type "الحفار" and hit "الحفَّار". Matches against invoice number,
// supplier name/phone, note, or any product name on the invoice.
const PurchaseHistory = ({ refreshKey, onChange, products }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = `${today.slice(0, 7)}-01`;
  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [detailId, setDetailId] = useState(null);

  const fetchList = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await axios.get(`${API}/admin/purchase-invoices`, { params });
      setList(data || []);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchList(); }, [refreshKey, dateFrom, dateTo]);

  // Build a single searchable string per invoice so we don't rebuild it
  // per keystroke: number + supplier + phone + note + product names.
  const haystacks = useMemo(() => list.map((inv) => {
    const productNames = (inv.items || []).map((it) => it.name_ar || it.name || "").join(" ");
    return `${inv.invoice_number || ""} ${inv.supplier_name || ""} ${inv.supplier_phone || ""} ${inv.note || ""} ${productNames}`;
  }), [list]);

  const filtered = useMemo(() => {
    if (!deferredSearch.trim()) return list;
    return list.filter((_, i) => smartMatch(haystacks[i], deferredSearch));
  }, [list, haystacks, deferredSearch]);

  // Summary chips — computed on the filtered set so the numbers reflect
  // what the admin is currently looking at.
  const summary = useMemo(() => {
    const s = { count: filtered.length, total: 0, paid: 0, remaining: 0 };
    filtered.forEach((inv) => {
      s.total += Number(inv.total || 0);
      s.paid += Number(inv.amount_paid || 0);
      s.remaining += Number(inv.amount_remaining || 0);
    });
    return s;
  }, [filtered]);

  const grouped = useMemo(() => {
    const out = {};
    filtered.forEach((inv) => {
      const day = (inv.created_at || "").slice(0, 10);
      out[day] = out[day] || [];
      out[day].push(inv);
    });
    return out;
  }, [filtered]);

  return (
    <div className="space-y-3" data-testid="purchase-history">
      {/* Colored filter card — gradient header + search + dates */}
      <div className="rounded-2xl overflow-hidden shadow-lg" style={{ background: "linear-gradient(135deg, #1E3A5F, #3B82F6)" }}>
        <div className="p-4 text-white">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
              <Receipt className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-black text-base">قوائم الشراء السابقة</h2>
              <p className="text-[11px] opacity-80">بحث دقيق برقم الفاتورة، المُجهّز، الملاحظات، أو اسم أي منتج</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
            <SumChip label="عدد الفواتير" value={summary.count} tone="blue" ltr={false} />
            <SumChip label="إجمالي" value={summary.total} tone="cyan" />
            <SumChip label="مدفوع" value={summary.paid} tone="green" />
            <SumChip label="الباقي" value={summary.remaining} tone="red" />
          </div>
        </div>

        <div className="p-3 bg-white space-y-2">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ابحث عن فاتورة (رقم / شركة / منتج / ملاحظة)..."
              data-testid="purchase-history-search"
              className="w-full pr-9 pl-3 py-2.5 rounded-xl border-2 border-blue-100 focus:outline-none focus:border-blue-400 text-sm"
            />
          </div>
          <div className="flex gap-2 items-end flex-wrap">
            <div className="flex-1 min-w-[100px]">
              <label className="text-[10px] text-slate-500 block">من</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="history-from"
                className="w-full px-2 py-1.5 rounded-lg border-2 border-blue-100 text-xs focus:outline-none focus:border-blue-400" />
            </div>
            <div className="flex-1 min-w-[100px]">
              <label className="text-[10px] text-slate-500 block">إلى</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="history-to"
                className="w-full px-2 py-1.5 rounded-lg border-2 border-blue-100 text-xs focus:outline-none focus:border-blue-400" />
            </div>
            <button onClick={() => { setDateFrom(""); setDateTo(""); setSearch(""); }}
              className="text-xs text-orange-700 bg-orange-50 hover:bg-orange-100 font-bold px-3 py-1.5 rounded-full">
              مسح
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin inline text-blue-600" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center text-slate-500">
          {list.length === 0 ? "لا توجد فواتير شراء" : "لا نتائج تطابق البحث"}
        </div>
      ) : (
        Object.keys(grouped).sort().reverse().map((day) => (
          <div key={day} className="space-y-1.5">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-black text-white px-3 py-1 rounded-full flex items-center gap-1 shadow-sm"
                style={{ background: "linear-gradient(90deg, #2563EB, #3B82F6)" }} dir="ltr">
                <Calendar className="w-3 h-3" /> {day}
              </span>
              <span className="text-[11px] text-slate-500 font-bold">({grouped[day].length} فاتورة)</span>
            </div>
            <div className="space-y-2">
              {grouped[day].map((inv) => {
                const fullyPaid = Number(inv.amount_remaining || 0) <= 0;
                return (
                  <button
                    key={inv.id}
                    onClick={() => setDetailId(inv.id)}
                    data-testid={`history-invoice-${inv.id}`}
                    className="w-full bg-white hover:shadow-lg border-2 border-slate-100 hover:border-blue-400 rounded-2xl p-3 text-right transition-all group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2.5 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center shrink-0 transition-colors">
                          <Receipt className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-black" dir="ltr">
                              #{inv.invoice_number}
                            </span>
                            <span className="font-black text-sm text-slate-800 truncate">{inv.supplier_name || "—"}</span>
                          </div>
                          <p className="text-[10px] text-slate-500 mt-0.5" dir="ltr">{fmtDate(inv.created_at)}</p>
                          {(inv.items?.length > 0) && (
                            <p className="text-[10px] text-slate-500 mt-0.5">
                              📦 {inv.items.length} مواد
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-left shrink-0 min-w-[90px]">
                        <p className="font-black text-base text-emerald-600" dir="ltr">{fmt(inv.total)}</p>
                        {fullyPaid ? (
                          <span className="inline-block mt-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                            ✓ مدفوع بالكامل
                          </span>
                        ) : (
                          <span className="inline-block mt-0.5 text-[9px] font-bold text-red-700 bg-red-50 px-1.5 py-0.5 rounded-full" dir="ltr">
                            باقي {fmt(inv.amount_remaining)}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}

      {detailId && (
        <InvoiceDetailModal
          invoiceId={detailId}
          products={products}
          onClose={() => setDetailId(null)}
          onDeleted={() => { fetchList(); onChange && onChange(); }}
        />
      )}
    </div>
  );
};

const SumChip = ({ label, value, tone, ltr = true }) => {
  const toneMap = {
    blue: "bg-white/15",
    cyan: "bg-cyan-500/25",
    green: "bg-emerald-500/25",
    red: "bg-rose-500/25",
  };
  return (
    <div className={`rounded-xl p-2 backdrop-blur-sm text-white ${toneMap[tone] || "bg-white/15"}`}>
      <p className="text-[10px] opacity-85">{label}</p>
      <p className="font-black text-sm mt-0.5" dir={ltr ? "ltr" : "rtl"}>
        {typeof value === "number" && ltr ? fmt(value) : value}
      </p>
    </div>
  );
};

// =============== Supplier Voucher Form ===============
const SupplierVoucherForm = ({ supplier, kind, onClose, onSaved }) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const meta = kind === "payment_out"
    ? { label: "سند دفع إلى المُجهّز", color: "#F5A623", icon: HandCoins, hint: "أعطيت المُجهّز مبلغًا (يقلّل الذمّة)" }
    : { label: "سند قبض من المُجهّز", color: "#7ED321", icon: Banknote, hint: "استرجعت من المُجهّز مبلغًا (يقلّل الذمّة)" };
  const Icon = meta.icon;

  const submit = async () => {
    const a = Number(amount);
    if (!a || a <= 0) { toast.error("أدخل مبلغًا صحيحًا"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/suppliers/${supplier.id}/vouchers`, { kind, amount: a, note: note.trim() });
      toast.success(`تم تسجيل ${meta.label}`);
      onSaved && onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "فشل"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3" onClick={onClose} data-testid={`supplier-voucher-modal-${kind}`} dir="rtl">
      <div className="bg-white rounded-3xl w-full max-w-md p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center pb-3 mb-3 border-b-2 border-[#E4EBE6]">
          <h3 className="font-bold text-lg flex items-center gap-2" style={{ color: meta.color }}>
            <Icon className="w-5 h-5" /> {meta.label}
          </h3>
          <button onClick={onClose} data-testid="supplier-voucher-close"><X className="w-5 h-5 text-[#5C6E64]" /></button>
        </div>
        <div className="bg-[#F0F9FF] border border-[#3B82F6] rounded-xl px-3 py-2 mb-3">
          <p className="text-xs text-[#5C6E64]">المُجهّز</p>
          <p className="font-bold text-[#1E3A5F]">{supplier.name}</p>
          <p className="text-[10px] text-[#5C6E64] mt-1">{meta.hint}</p>
        </div>
        <div className="bg-[#FFF8EC] border border-[#F5A623] rounded-xl px-3 py-2 mb-3 text-center">
          <p className="text-[10px] text-[#5C6E64]">الذمّة الحالية على المُجهّز</p>
          <p className="font-black text-lg text-[#a06e10]" dir="ltr">{fmt(supplier.debt)} د.ع</p>
        </div>
        <label className="text-xs font-bold block mb-1">المبلغ (د.ع)</label>
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="supplier-voucher-amount" className="w-full px-3 py-2 rounded-xl border-2 border-[#3B82F6] mb-3 text-lg font-bold" dir="ltr" placeholder="0" />
        <label className="text-xs font-bold block mb-1">ملاحظة</label>
        <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="supplier-voucher-note" placeholder="ملاحظة (اختياري)" className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] mb-4" />
        <button onClick={submit} disabled={busy} data-testid="supplier-voucher-submit" className="w-full text-white font-bold py-2.5 rounded-full disabled:opacity-50" style={{ backgroundColor: meta.color }}>
          {busy ? "جاري الحفظ..." : "حفظ السند"}
        </button>
      </div>
    </div>
  );
};

// =============== Supplier Statement Modal ===============
const SupplierStatementModal = ({ supplierId, onClose, onChange }) => {
  const [data, setData] = useState(null);
  const [voucher, setVoucher] = useState(null); // kind

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/suppliers/${supplierId}/statement`);
      setData(d);
    } catch { toast.error("فشل"); onClose(); }
  };
  useEffect(() => { load(); }, [supplierId]);

  const removeVoucher = async (vid) => {
    if (!(await confirmDialog("حذف هذا السند؟"))) return;
    try {
      await axios.delete(`${API}/admin/supplier-vouchers/${vid}`);
      toast.success("تم الحذف");
      load();
      onChange && onChange();
    } catch { toast.error("فشل"); }
  };

  if (!data) return <div className="fixed inset-0 z-[105] bg-black/70 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>;
  const s = data.supplier;

  return (
    <div className="fixed inset-0 z-[105] bg-black/70 flex items-center justify-center p-2" onClick={onClose} data-testid="supplier-statement-modal" dir="rtl">
      <div className="bg-[#F8FAFC] rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white p-4 sticky top-0 flex justify-between items-start">
          <div>
            <h3 className="font-bold text-xl">{s.name}</h3>
            <p className="text-xs opacity-90">{s.phone || "—"} · {s.address || "—"}</p>
          </div>
          <button onClick={onClose} data-testid="supplier-statement-close" className="bg-white/20 rounded-full p-1.5"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-gradient-to-l from-[#E85D45] to-[#d54d35] text-white rounded-2xl p-3" data-testid="supplier-debt-card">
              <p className="text-[10px] opacity-90">الذمّة الحالية</p>
              <p className="text-2xl font-black" dir="ltr">{fmt(s.debt)}</p>
              <p className="text-[10px] opacity-80">عدد الفواتير: <span dir="ltr">{s.invoice_count}</span></p>
            </div>
            <div className="bg-gradient-to-l from-[#7ED321] to-[#6cb91d] text-white rounded-2xl p-3">
              <p className="text-[10px] opacity-90">إجمالي المشتريات</p>
              <p className="text-2xl font-black" dir="ltr">{fmt(s.total_invoiced)}</p>
              <p className="text-[10px] opacity-80">مدفوع: <span dir="ltr">{fmt(s.total_paid_at_invoice + s.total_payment_out + s.total_receipt_in)}</span></p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-1">
            <button onClick={() => setVoucher("payment_out")} data-testid="supplier-pay-btn" className="bg-[#F5A623] hover:bg-[#dc9320] text-white text-xs font-bold py-2 rounded-xl flex items-center justify-center gap-1">
              <HandCoins className="w-3.5 h-3.5" /> سند دفع
            </button>
            <button onClick={() => setVoucher("receipt_in")} data-testid="supplier-receive-btn" className="bg-[#7ED321] hover:bg-[#6cb91d] text-white text-xs font-bold py-2 rounded-xl flex items-center justify-center gap-1">
              <Banknote className="w-3.5 h-3.5" /> سند قبض
            </button>
          </div>

          <div className="bg-white rounded-2xl p-3 border border-[#E4EBE6]">
            <h4 className="font-bold text-[#1E3A5F] mb-2">السندات</h4>
            {data.vouchers.length === 0 ? <p className="text-xs text-[#5C6E64] text-center py-2">لا توجد سندات</p> : (
              <div className="space-y-1">
                {data.vouchers.map((v) => {
                  const isOut = v.kind === "payment_out";
                  return (
                    <div key={v.id} className="flex justify-between bg-[#F8FAFC] rounded-xl p-2 border border-[#E4EBE6]">
                      <div>
                        <p className="text-xs font-bold" style={{ color: isOut ? "#F5A623" : "#7ED321" }}>
                          {isOut ? "دفع للمُجهّز" : "قبض من المُجهّز"}
                        </p>
                        {v.note && <p className="text-[10px] text-[#5C6E64]">{v.note}</p>}
                        <p className="text-[9px] text-[#5C6E64]" dir="ltr">{fmtDate(v.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-sm" style={{ color: isOut ? "#F5A623" : "#7ED321" }} dir="ltr">{fmt(v.amount)}</p>
                        <button onClick={() => removeVoucher(v.id)} className="text-[#E85D45] hover:bg-[#E85D45]/10 rounded p-1"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl p-3 border border-[#E4EBE6]">
            <h4 className="font-bold text-[#1E3A5F] mb-2">فواتير الشراء</h4>
            {data.invoices.length === 0 ? <p className="text-xs text-[#5C6E64] text-center py-2">لا توجد فواتير</p> : (
              <div className="space-y-1">
                {data.invoices.map((inv) => (
                  <div key={inv.id} className="flex justify-between bg-[#F8FAFC] rounded-xl p-2 border border-[#E4EBE6] text-xs">
                    <div>
                      <p className="font-bold">#<span dir="ltr">{inv.invoice_number}</span></p>
                      <p className="text-[10px] text-[#5C6E64]" dir="ltr">{fmtDate(inv.created_at)}</p>
                    </div>
                    <div className="text-left">
                      <p className="font-bold" dir="ltr">{fmt(inv.total)}</p>
                      {inv.amount_remaining > 0 && <p className="text-[10px] text-[#E85D45]" dir="ltr">باقي {fmt(inv.amount_remaining)}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {voucher && (
          <SupplierVoucherForm
            supplier={s}
            kind={voucher}
            onClose={() => setVoucher(null)}
            onSaved={() => { load(); onChange && onChange(); }}
          />
        )}
      </div>
    </div>
  );
};

// =============== Suppliers List ===============
const SuppliersList = ({ suppliers, onChange }) => {
  const [q, setQ] = useState("");
  const [detailId, setDetailId] = useState(null);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return suppliers;
    return suppliers.filter((s) => (s.name || "").toLowerCase().includes(t) || (s.phone || "").includes(t));
  }, [suppliers, q]);

  return (
    <div className="space-y-3" data-testid="suppliers-list">
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="suppliers-search" placeholder="ابحث عن مُجهّز..." className="w-full pr-9 pl-4 py-2 rounded-full bg-white border-2 border-[#3B82F6]" />
      </div>
      {filtered.length === 0 ? (
        <div className="bg-white border-2 border-dashed rounded-2xl p-8 text-center text-[#5C6E64]">
          <Truck className="w-10 h-10 mx-auto opacity-30 mb-2" />
          لا يوجد مُجهّزون بعد. سيُنشأ المُجهّز تلقائيًا عند حفظ أوّل فاتورة شراء.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((s) => (
            <button
              key={s.id}
              onClick={() => setDetailId(s.id)}
              data-testid={`supplier-card-${s.id}`}
              className="w-full bg-white hover:bg-[#F0F9FF] border-2 border-[#E4EBE6] hover:border-[#3B82F6] rounded-2xl p-3 flex items-center justify-between text-right transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-10 h-10 bg-[#1E3A5F] text-white rounded-full flex items-center justify-center shrink-0">
                  <Truck className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold truncate">{s.name}</p>
                  <p className="text-[10px] text-[#5C6E64]">{s.phone || "—"} · {s.invoice_count} فاتورة</p>
                </div>
              </div>
              <div className="text-left shrink-0">
                <p className="text-[10px] text-[#5C6E64]">الذمّة</p>
                <p className={`font-black text-base ${s.debt > 0 ? "text-[#E85D45]" : s.debt < 0 ? "text-[#7ED321]" : "text-[#5C6E64]"}`} dir="ltr">{fmt(s.debt)}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {detailId && (
        <SupplierStatementModal
          supplierId={detailId}
          onClose={() => setDetailId(null)}
          onChange={onChange}
        />
      )}
    </div>
  );
};

// =============== Low Stock Notifications Modal ===============
export const LowStockModal = ({ onClose, onDone }) => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState({}); // {id: product}

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/low-stock`);
      setData(d);
    } catch { toast.error("فشل التحميل"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const t = q.trim().toLowerCase();
    if (!t) return data.items;
    return data.items.filter((p) => (p.name_ar || "").toLowerCase().includes(t));
  }, [data, q]);

  const togglePick = (p) => {
    setPicked((prev) => {
      const next = { ...prev };
      if (next[p.id]) {
        delete next[p.id];
        toast.message("تمت إزالة المنتج من القائمة");
      } else {
        next[p.id] = p;
        toast.success(`أضيف "${p.name_ar}" إلى قائمة الشراء`);
      }
      return next;
    });
  };

  const finishAndGo = () => {
    const list = Object.values(picked);
    onDone && onDone(list);
    onClose();
  };

  const pickedCount = Object.keys(picked).length;

  return (
    <div className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-3" data-testid="low-stock-modal" dir="rtl">
      {/* NOTE: outer overlay does NOT close on click — user must close manually so they can buy multiple items */}
      <div className="bg-white rounded-3xl w-full max-w-xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="bg-gradient-to-l from-[#E85D45] to-[#d54d35] text-white p-4 sticky top-0 flex justify-between items-start shrink-0">
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> تنبيهات المخزن المنخفض</h3>
            <p className="text-[10px] opacity-90 mt-1">يتم التنبيه عند ≤ <span dir="ltr">{data?.threshold || 5}</span> كراتين · النافذة تبقى مفتوحة حتى تُغلقها يدوياً</p>
          </div>
          <button onClick={onClose} data-testid="low-stock-close" className="bg-white/20 hover:bg-white/30 rounded-full p-1.5"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-3 space-y-2 overflow-y-auto flex-1">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="low-stock-search" placeholder="ابحث عن منتج..." className="w-full pr-9 pl-4 py-2 rounded-full border-2 border-[#3B82F6]" />
          </div>
          {!data ? <div className="text-center py-6"><Loader2 className="w-6 h-6 animate-spin inline" /></div> : filtered.length === 0 ? (
            <div className="bg-[#ECFDF5] border border-[#7ED321] rounded-2xl p-6 text-center">
              <p className="text-sm text-[#15803d] font-bold">✓ كل المنتجات بكميات جيدة</p>
            </div>
          ) : (
            filtered.map((p) => {
              const isPicked = !!picked[p.id];
              return (
                <div
                  key={p.id}
                  data-testid={`low-stock-row-${p.id}`}
                  className={`rounded-xl p-2 flex items-center gap-2 border transition-colors ${isPicked ? "bg-[#ECFDF5] border-[#7ED321]" : "bg-[#FFF8EC] border-[#F5A623]/30"}`}
                >
                  {p.image_path ? (
                    <img src={`${API}/files/${p.image_path}`} className="w-12 h-12 rounded-lg object-cover" alt="" />
                  ) : <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center"><Package className="w-4 h-4" /></div>}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold truncate flex items-center gap-1">
                      {isPicked && <span className="text-[#15803d]">✓</span>}
                      {p.name_ar}
                    </p>
                    <p className="text-[10px] text-[#5C6E64]">
                      المخزن: <span dir="ltr" className="font-black text-[#E85D45]">{p.stock}</span> · شراء <span dir="ltr">{fmt(p.cost_price)}</span> · بيع <span dir="ltr">{fmt(p.price)}</span>
                    </p>
                  </div>
                  <button
                    onClick={() => togglePick(p)}
                    data-testid={`low-stock-buy-${p.id}`}
                    className={`text-[10px] font-bold py-1.5 px-3 rounded-full flex items-center gap-1 shrink-0 ${
                      isPicked
                        ? "bg-[#5C6E64] hover:bg-[#475A50] text-white"
                        : "bg-[#7ED321] hover:bg-[#6cb91d] text-white"
                    }`}
                  >
                    {isPicked ? <><X className="w-3 h-3" /> إزالة</> : <><ShoppingCart className="w-3 h-3" /> شراء</>}
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Sticky footer with picked counter + finish button */}
        <div className="bg-white border-t-2 border-[#E4EBE6] p-3 flex items-center justify-between gap-2 shrink-0">
          <div className="text-xs">
            <p className="text-[#5C6E64]">مختار للشراء</p>
            <p data-testid="low-stock-picked-count" className="font-black text-lg text-[#1E3A5F]" dir="ltr">{pickedCount}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} data-testid="low-stock-keep-open" className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] text-xs font-bold py-2 px-3 rounded-full">
              إغلاق فقط
            </button>
            <button
              onClick={finishAndGo}
              disabled={pickedCount === 0}
              data-testid="low-stock-finish-btn"
              className="bg-[#7ED321] hover:bg-[#6cb91d] disabled:bg-[#BAE6FD] disabled:cursor-not-allowed text-white text-xs font-bold py-2 px-4 rounded-full flex items-center gap-1"
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              انتقل للشراء (<span dir="ltr">{pickedCount}</span>)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============== Main PurchaseTab ===============
const PurchaseTab = ({ presetProductIdsFromBell, onConsumedPreset }) => {
  const [section, setSection] = useState("home"); // home | new | history | suppliers
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const reloadProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/products`);
      setProducts(data || []);
    } catch { /* silent */ }
  };
  const reloadSuppliers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/suppliers`);
      setSuppliers(data || []);
    } catch { /* silent */ }
  };

  useEffect(() => { reloadProducts(); reloadSuppliers(); }, [refreshKey]);

  // If low-stock products were sent from the bell, jump directly to new-purchase
  useEffect(() => {
    if (presetProductIdsFromBell && presetProductIdsFromBell.length > 0) {
      setSection("new");
    }
  }, [presetProductIdsFromBell]);

  if (section === "home") {
    return (
      <div className="space-y-3" data-testid="purchase-home" dir="rtl">
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <ShoppingCart className="w-5 h-5" />
            <h3 className="font-bold text-lg">إدارة الشراء</h3>
          </div>
          <p className="text-xs opacity-90">إنشاء فواتير شراء من المُجهّزين، متابعة الذمم، وتنبيهات المخزن المنخفض.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button onClick={() => setSection("new")} data-testid="enter-new-purchase" className="bg-gradient-to-l from-[#7ED321] to-[#6cb91d] text-white rounded-2xl p-5 hover:shadow-xl flex flex-col items-center gap-2 min-h-[120px]">
            <ShoppingCart className="w-9 h-9" />
            <span className="font-bold">قائمة شراء جديدة</span>
            <span className="text-[10px] opacity-90">إضافة فاتورة شراء جديدة</span>
          </button>
          <button onClick={() => setSection("history")} data-testid="enter-purchase-history" className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-5 hover:shadow-xl flex flex-col items-center gap-2 min-h-[120px]">
            <History className="w-9 h-9" />
            <span className="font-bold">القوائم السابقة</span>
            <span className="text-[10px] opacity-90">عرض الفواتير السابقة بالتاريخ</span>
          </button>
          <button onClick={() => setSection("suppliers")} data-testid="enter-suppliers" className="bg-gradient-to-l from-[#F5A623] to-[#dc9320] text-white rounded-2xl p-5 hover:shadow-xl flex flex-col items-center gap-2 min-h-[120px]">
            <Truck className="w-9 h-9" />
            <span className="font-bold">المُجهّزون والذمم</span>
            <span className="text-[10px] opacity-90">سندات دفع / قبض + كشف حساب</span>
          </button>
        </div>
      </div>
    );
  }

  const backHome = (
    <button onClick={() => { setSection("home"); setRefreshKey((k) => k + 1); }} data-testid="purchase-back-home" className="bg-[#F3F6F4] text-[#5C6E64] hover:bg-[#E4EBE6] rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1 mb-2">
      <ArrowLeft className="w-3 h-3 rotate-180" /> رجوع
    </button>
  );

  if (section === "new") {
    return (
      <div data-testid="section-new-purchase" dir="rtl">
        {backHome}
        <NewPurchaseInvoice
          products={products}
          suppliers={suppliers}
          presetProductIds={presetProductIdsFromBell}
          onSaved={() => { reloadProducts(); reloadSuppliers(); onConsumedPreset && onConsumedPreset(); }}
        />
      </div>
    );
  }
  if (section === "history") {
    return (
      <div data-testid="section-history" dir="rtl">
        {backHome}
        <PurchaseHistory refreshKey={refreshKey} onChange={reloadProducts} products={products} />
      </div>
    );
  }
  return (
    <div data-testid="section-suppliers" dir="rtl">
      {backHome}
      <SuppliersList suppliers={suppliers} onChange={() => setRefreshKey((k) => k + 1)} />
    </div>
  );
};

export default PurchaseTab;
